import {
  convertKeyToLabel,
  OntologyContextType,
  OntologyField,
  OntologyRolodexEntryType,
  useOntology,
} from 'features/dynamicOntology'
import reduce from 'lodash/reduce'
import uniq from 'lodash/uniq'
import {
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react'
import styled from 'styled-components'

import {
  TwoStepDropdown,
  TwoStepFieldSelectOption,
} from './components/TwoStepSelect'

const Container = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: left;
  gap: 1rem;
`

type GroupByFieldSelectProps = {
  label?: ReactNode
  fields?: OntologyContextType['fields']
  selectedField: OntologyField
  setSelectedField: Dispatch<SetStateAction<OntologyField>>
  placeholder?: string
  width?: string
}

export const DrilldownTypeAndFieldSelect: FC<GroupByFieldSelectProps> = ({
  label,
  selectedField,
  setSelectedField,
  fields: propFields,
  placeholder,
  width = '240px',
}) => {
  const { fields: hookFields, entryTypeNames } = useOntology()
  const fields = useMemo(
    () => propFields ?? hookFields,
    [hookFields, propFields]
  )

  const primaryOptions = useMemo<ReadonlyArray<TwoStepFieldSelectOption>>(
    () =>
      uniq(Object.values(fields).map((field) => field.entryType))
        .map((entryType) => ({
          value: entryType,
          label: entryTypeNames[entryType] ?? convertKeyToLabel(entryType),
        }))
        .toSorted(),
    [fields, entryTypeNames]
  )
  const [primarySelection, setPrimarySelection] =
    useState<OntologyRolodexEntryType>(primaryOptions[0]?.value)

  const secondaryOptions = useMemo<
    ReadonlyMap<
      OntologyRolodexEntryType,
      ReadonlyArray<TwoStepFieldSelectOption>
    >
  >(
    () =>
      reduce(
        Object.entries(fields),
        (acc, [field, { entryType, name }]) => {
          const option = { label: name, value: field }
          const fieldList = acc.get(entryType) ?? []
          const newAcc = new Map(acc)
          newAcc.set(entryType, [...fieldList, option])
          return newAcc
        },
        new Map()
      ),
    [fields]
  )

  useEffect(() => {
    setPrimarySelection((prev) => fields[selectedField]?.entryType ?? prev)
  }, [fields, primaryOptions, secondaryOptions, selectedField])

  return (
    <Container>
      {label ?? <></>}
      <TwoStepDropdown
        primaryOptions={primaryOptions}
        secondaryOptions={secondaryOptions}
        primarySelection={primarySelection}
        setPrimarySelection={setPrimarySelection}
        secondarySelection={selectedField}
        setSecondarySelection={setSelectedField}
        placeholder={placeholder}
        width={width}
      />
    </Container>
  )
}
